/**
 * **Penjualan hari ini** — *"apa yang laku"*, dibaca dari layar kasir.
 *
 * ### ⭐ Bedanya dengan X-report
 * X-report menjawab *"berapa yang harus ada di laci"* — ia soal UANG dan sesi kas. Layar ini
 * menjawab pertanyaan yang lain: **barang apa yang bergerak**. Keduanya sering dibutuhkan orang
 * yang sama dalam sepuluh menit yang sama, tapi menyatukannya membuat lembar setoran penuh baris
 * menu — dan yang menandatangani setoran berhenti membacanya.
 *
 * ### ⛔ Layar tak menjumlahkan apa pun
 * Ringkasan (bill, tamu, total) DAN porsi tiap item dihitung server. Layar yang menjumlahkan ulang
 * akan berbeda pembulatannya dari X-report di layar sebelah — dua angka untuk satu hari, dan kasir
 * berhenti mempercayai keduanya.
 *
 * ⚠️ Kotor · dikembalikan · bersih ditampilkan berdampingan: item yang laku keras TAPI banyak
 * dikembalikan adalah sinyal paling berharga di daftar ini, dan ia hilang kalau cuma bersih yang
 * tampil.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { SalesToday as Data } from '../api/client';
import { formatRupiah } from '../format';

export interface SalesTodayProps {
  readonly outletCode: string;
  /** Tanggal usaha SESI — bukan jam tablet. Outlet tutup 02:00 (§2). */
  readonly businessDate?: string;
  readonly onClose: () => void;
}

export function SalesToday(p: SalesTodayProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void api.salesToday(p.outletCode, p.businessDate)
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => {
        /* ⛔ Pesan server apa adanya — `AUTH-403-NO_PERMISSION` sudah menyebut izin yang kurang. */
        if (alive) setError(e instanceof Error ? e.message : String(e));
      });
    return () => { alive = false; };
  }, [p.businessDate, p.outletCode]);

  return (
    <div className="dialog sales-today" role="dialog" data-testid="sales-today">
      <h2>{t('cashier.sales.title')}</h2>

      {error !== null && <p className="error" data-testid="sales-error">{error}</p>}

      {data !== null && (
        <>
          <dl className="sales-summary" data-testid="sales-summary">
            <div>
              <dt>{t('cashier.sales.bills')}</dt>
              <dd data-testid="sales-bills">{data.bills}</dd>
            </div>
            <div>
              <dt>{t('cashier.sales.guests')}</dt>
              <dd>{data.guests}</dd>
            </div>
            <div>
              <dt>{t('cashier.sales.total')}</dt>
              <dd data-testid="sales-total"><b>{formatRupiah(data.grandTotal)}</b></dd>
            </div>
          </dl>

          {data.items.length === 0 ? (
            <p className="empty" data-testid="sales-empty">{t('cashier.sales.empty')}</p>
          ) : (
            <ul className="sales-list">
              {data.items.map((it) => (
                <li key={it.productCode} data-testid={`sales-${it.productCode}`}>
                  <span className="sales-qty">{Number(it.qtySold).toFixed(0)}×</span>
                  <span className="sales-name">{it.productName}</span>
                  {/*
                    ⭐ Yang dikembalikan ditandai MERAH di baris yang sama. Item laku-tapi-banyak-
                    dikembalikan (dapur bermasalah, porsi meleset) terlihat sama dengan item biasa
                    kalau cuma yang bersih ditampilkan.
                  */}
                  {Number(it.qtyRefunded) > 0 && (
                    <span className="sales-refunded" data-testid={`sales-refunded-${it.productCode}`}>
                      −{Number(it.qtyRefunded).toFixed(0)}
                    </span>
                  )}
                  <span className="sales-value">{formatRupiah(it.netAfterRefund)}</span>
                  {/* ⛔ Porsi dari SERVER — dua layar yang membaginya sendiri akan berbeda. */}
                  <span className="sales-share">{(Number(it.share) * 100).toFixed(0)}%</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div className="dialog-actions">
        <button type="button" className="action-primary" data-testid="sales-close"
                onClick={p.onClose}>
          {t('cashier.sales.close')}
        </button>
      </div>
    </div>
  );
}
